// utils/encryption.js
/**
 * Frontend encryption utilities for handling encrypted API keys
 * This provides client-side decryption of API keys received from the backend
 */

/**
 * Decrypt an API key received from the backend
 * Uses XOR obfuscation that matches the backend implementation
 * 
 * @param {string} encryptedApiKey - The encrypted API key from backend
 * @returns {string} - The decrypted API key
 */
export function decryptApiKey(encryptedApiKey) {
  if (!encryptedApiKey) {
    return '';
  }
  
  try {
    // Convert URL-safe base64 to standard base64 first
    const standardBase64 = encryptedApiKey.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding if necessary
    const paddedBase64 = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4);
    // Base64 decode
    const obfuscated = atob(paddedBase64);
    
    // XOR deobfuscation (same key phrase as backend)
    const keyPhrase = "AI_ASSIST_ENCRYPTION_2025";
    let decrypted = "";
    
    for (let i = 0; i < obfuscated.length; i++) {
      const keyChar = keyPhrase[i % keyPhrase.length];
      const decryptedChar = String.fromCharCode(
        obfuscated.charCodeAt(i) ^ keyChar.charCodeAt(0)
      );
      decrypted += decryptedChar;
    }
    
    return decrypted;
  } catch (error) {
    console.error('Failed to decrypt API key:', error);
    return '';
  }
}

/**
 * Encrypt an API key before sending to the backend
 * Uses XOR obfuscation that matches the backend implementation
 * 
 * @param {string} apiKey - The plain text API key
 * @returns {string} - The encrypted API key
 */
export function encryptApiKey(apiKey) {
  if (!apiKey) {
    return '';
  }
  
  try {
    // XOR obfuscation (same key phrase as backend)
    const keyPhrase = "AI_ASSIST_ENCRYPTION_2025";
    let obfuscated = "";
    
    for (let i = 0; i < apiKey.length; i++) {
      const keyChar = keyPhrase[i % keyPhrase.length];
      const obfuscatedChar = String.fromCharCode(
        apiKey.charCodeAt(i) ^ keyChar.charCodeAt(0)
      );
      obfuscated += obfuscatedChar;
    }
    
    // Base64 encode the result and convert to URL-safe format
    const base64 = btoa(obfuscated);
    return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  } catch (error) {
    console.error('Failed to encrypt API key:', error);
    return '';
  }
}

/**
 * Check if a string appears to be an encrypted API key
 * This is a simple heuristic - encrypted keys should be base64 encoded
 * 
 * @param {string} value - The value to check
 * @returns {boolean} - True if it appears to be encrypted
 */
export function isEncryptedApiKey(value) {
  if (!value || typeof value !== 'string') {
    return false;
  }
  
  // Check if it's URL-safe base64 encoded (simple heuristic)
  try {
    // Convert URL-safe base64 to standard base64
    const standardBase64 = value.replace(/-/g, '+').replace(/_/g, '/');
    const paddedBase64 = standardBase64 + '='.repeat((4 - standardBase64.length % 4) % 4);
    const decoded = atob(paddedBase64);
    return decoded.length > 15 && value.length > 20; // API keys are typically long
  } catch (error) {
    return false;
  }
}

/**
 * Sanitize API key for display purposes
 * Shows only first 7 and last 4 characters with asterisks in between
 * 
 * @param {string} apiKey - The API key to sanitize
 * @returns {string} - The sanitized API key for display
 */
export function sanitizeApiKeyForDisplay(apiKey) {
  if (!apiKey || apiKey.length < 12) {
    return '';
  }
  
  const start = apiKey.substring(0, 7);
  const end = apiKey.substring(apiKey.length - 4);
  const middle = '*'.repeat(Math.min(apiKey.length - 11, 20));
  
  return `${start}${middle}${end}`;
}